import { Goal, ID } from '@griply/types';

export type GetSubGoalsOptions = {
  goals: Goal[];
  recursive?: boolean;
};

export const getSubGoals = (
  id: ID | undefined,
  { goals, recursive = false }: GetSubGoalsOptions,
): Goal[] => {
  if (!id) {
    return [];
  }

  const parentToChildrenMap = new Map<ID, Goal[]>();
  goals.forEach((goal) => {
    if (!goal.parentIds?.length) {
      return;
    }
    const parentId = goal.parentIds[goal.parentIds.length - 1];
    const existing = parentToChildrenMap.get(parentId) ?? [];
    existing.push(goal);
    parentToChildrenMap.set(parentId, existing);
  });

  const directSubGoals = parentToChildrenMap.get(id) || [];

  if (!recursive) {
    return directSubGoals;
  }

  const processedIds = new Set<ID>(directSubGoals.map((goal) => goal.id));

  const collectDescendants = (currentLevel: Goal[]): Goal[] => {
    if (!currentLevel.length) {
      return [];
    }

    const nextLevel = currentLevel.flatMap((goal) =>
      (parentToChildrenMap.get(goal.id) || []).filter((child) => {
        if (processedIds.has(child.id)) {
          return false;
        }
        processedIds.add(child.id);
        return true;
      }),
    );

    return [...nextLevel, ...collectDescendants(nextLevel)];
  };

  return [...directSubGoals, ...collectDescendants(directSubGoals)];
};
